import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { getJiraComments, getJiraCommentsStats, getJiraCommentEmployees, formatApiError } from '../services/api';
import { getDateLocale } from '../i18n';

function employeeChipLabel(user) {
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return name || user.email || `User #${user.id}`;
}

function getJiraCommentDateRange(preset) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const pad = (n) => String(n).padStart(2, '0');
  const toStr = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

  switch (preset) {
    case 'today':
      return { date_from: toStr(today), date_to: toStr(today) };
    case 'yesterday': {
      const day = new Date(today);
      day.setDate(day.getDate() - 1);
      return { date_from: toStr(day), date_to: toStr(day) };
    }
    case 'this_week': {
      const from = new Date(today);
      const weekday = from.getDay();
      const daysFromMonday = weekday === 0 ? 6 : weekday - 1;
      from.setDate(from.getDate() - daysFromMonday);
      return { date_from: toStr(from), date_to: toStr(today) };
    }
    case 'this_month': {
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      return { date_from: toStr(from), date_to: toStr(today) };
    }
    default:
      return null;
  }
}

const JiraCommentsPage = ({
  selectedComment = null,
  onCommentSelect,
  onClearCommentSelection,
  listRefreshKey = 0,
}) => {
  const { t } = useTranslation(['admin', 'common']);
  const DATE_RANGE_OPTIONS = useMemo(() => [
    { value: '', label: t('jira.allDates') },
    { value: 'today', label: t('jira.today') },
    { value: 'yesterday', label: t('jira.yesterday') },
    { value: 'this_week', label: t('jira.thisWeek') },
    { value: 'this_month', label: t('jira.thisMonth') },
    { value: 'custom', label: t('jira.customRange') },
  ], [t]);
  const COMMENT_STAT_CARDS = useMemo(() => [
    {
      key: 'total',
      label: t('jira.totalComments'),
      subtitle: t('jira.matchingFilters'),
      icon: 'comment',
      iconClass: 'bg-blue-50 text-primary',
      valueKey: 'total',
    },
    {
      key: 'missing_imputation',
      label: t('jira.missingOperation'),
      subtitle: t('jira.awaitingLink'),
      icon: 'pending_actions',
      iconClass: 'bg-amber-50 text-amber-700',
      valueKey: 'missing_imputation',
    },
    {
      key: 'with_imputation_error',
      label: t('jira.imputationErrors'),
      subtitle: t('jira.autoLinkFailures'),
      icon: 'error',
      iconClass: 'bg-red-50 text-red-600',
      valueKey: 'with_imputation_error',
    },
    {
      key: 'imputations_created',
      label: t('jira.imputationsCreated'),
      subtitle: t('jira.commentsLinked'),
      icon: 'check_circle',
      iconClass: 'bg-emerald-50 text-emerald-700',
      valueKey: 'imputations_created',
    },
  ], [t]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [dateRangePreset, setDateRangePreset] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    employee: '',
    date_from: '',
    date_to: '',
  });

  const fetchComments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getJiraComments(filters, page, pageSize);
      const rows = Array.isArray(response?.results) ? response.results : [];
      setComments(rows);
      setTotalCount(response?.count || 0);
    } catch (err) {
      const formatted = formatApiError(err);
      setError(formatted.generalError || t('jira.loadCommentsFailed'));
      setComments([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const response = await getJiraCommentsStats(filters);
      setStats(response);
    } catch {
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, filters]);

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (dateRangePreset === 'custom') return;
    if (!dateRangePreset) {
      setFilters((prev) => ({ ...prev, date_from: '', date_to: '' }));
      return;
    }
    const range = getJiraCommentDateRange(dateRangePreset);
    if (range) {
      setFilters((prev) => ({ ...prev, ...range }));
    }
  }, [dateRangePreset]);

  const fetchEmployeesForDateRange = async () => {
    setEmployeesLoading(true);
    try {
      const response = await getJiraCommentEmployees({
        date_from: filters.date_from,
        date_to: filters.date_to,
      });
      const rows = Array.isArray(response?.results) ? response.results : [];
      setEmployees(rows);
      if (filters.employee && !rows.some((u) => String(u.id) === String(filters.employee))) {
        setFilters((prev) => ({ ...prev, employee: '' }));
      }
    } catch {
      setEmployees([]);
      if (filters.employee) {
        setFilters((prev) => ({ ...prev, employee: '' }));
      }
    } finally {
      setEmployeesLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployeesForDateRange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.date_from, filters.date_to]);

  useEffect(() => {
    if (listRefreshKey > 0) {
      fetchComments();
      fetchStats();
      fetchEmployeesForDateRange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listRefreshKey]);

  const formatDateTime = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString(getDateLocale());
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const startRow = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const endRow = Math.min(page * pageSize, totalCount);

  const clearFilters = () => {
    setDateRangePreset('');
    setFilters({ search: '', employee: '', date_from: '', date_to: '' });
    setPage(1);
  };

  const selectEmployeeFilter = (employeeId) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, employee: employeeId }));
  };

  const chipClass = (active) =>
    `inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
      active
        ? 'bg-primary text-white border-primary shadow-sm'
        : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-primary/10 hover:border-primary/30 hover:text-primary'
    }`;

  useEffect(() => {
    if (!selectedComment) return;
    const latestComment = comments.find((c) => c.id === selectedComment.id);
    if (!latestComment) {
      onClearCommentSelection?.();
      return;
    }
    if (JSON.stringify(latestComment) !== JSON.stringify(selectedComment)) {
      onCommentSelect?.(latestComment);
    }
  }, [comments, selectedComment, onClearCommentSelection, onCommentSelect]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex min-w-72 flex-col gap-2 px-1">
        <h1 className="text-slate-900 text-3xl font-bold leading-tight tracking-[-0.02em]">
          {t('jira.commentsTitle')}
        </h1>
        <p className="text-slate-500 text-base font-normal leading-normal">
          {t('jira.commentsSubtitle')}
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
          <input
            className="h-10 w-full min-w-0 rounded-lg border border-slate-200 bg-white px-4 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
            placeholder={t('jira.searchPlaceholder')}
            value={filters.search}
            onChange={(e) => {
              setPage(1);
              setFilters((prev) => ({ ...prev, search: e.target.value }));
            }}
          />
          <div className="relative w-full min-w-0">
            <select
              value={dateRangePreset}
              onChange={(e) => {
                setPage(1);
                setDateRangePreset(e.target.value);
              }}
              className="h-10 w-full appearance-none rounded-lg border border-slate-200 bg-white pl-4 pr-10 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary cursor-pointer"
              aria-label={t('jira.dateRange')}
            >
              {DATE_RANGE_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-500 text-[20px] pointer-events-none">
              arrow_drop_down
            </span>
          </div>
        </div>

        <AnimatePresence initial={false}>
          {dateRangePreset === 'custom' && (
            <motion.div
              key="custom-date-range"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
              className="overflow-hidden"
            >
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2, delay: 0.05 }}
                className="grid grid-cols-1 sm:grid-cols-2 gap-3"
              >
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-slate-600">{t('common:from')}</label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => {
                  setPage(1);
                  setFilters((prev) => ({ ...prev, date_from: e.target.value }));
                }}
                className="h-10 w-full rounded-lg border border-slate-200 bg-white px-4 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-slate-600">{t('common:to')}</label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => {
                  setPage(1);
                  setFilters((prev) => ({ ...prev, date_to: e.target.value }));
                }}
                className="h-10 w-full rounded-lg border border-slate-200 bg-white px-4 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
            </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
            {t('jira.filterByEmployee')}
          </p>
          {employeesLoading ? (
            <div className="flex flex-wrap gap-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-8 w-24 rounded-full bg-slate-100 animate-pulse" />
              ))}
            </div>
          ) : employees.length === 0 ? (
            <p className="text-sm text-slate-400">
              {t('jira.noEmployeesInRange')}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => selectEmployeeFilter('')}
                className={chipClass(!filters.employee)}
              >
                {t('jira.allEmployees')}
              </button>
              {employees.map((user) => {
                const id = String(user.id);
                const active = filters.employee === id;
                return (
                  <button
                    key={user.id}
                    type="button"
                    title={user.email || undefined}
                    onClick={() => selectEmployeeFilter(active ? '' : id)}
                    className={chipClass(active)}
                  >
                    {employeeChipLabel(user)}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <button
            type="button"
            onClick={clearFilters}
            className="h-10 px-4 text-sm font-medium text-primary hover:text-blue-700 transition-colors"
          >
            {t('jira.clearFilters')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {COMMENT_STAT_CARDS.map((card) => {
          const value = stats?.[card.valueKey] ?? 0;
          const subtitle = card.subtitleKey
            ? `${stats?.[card.subtitleKey] ?? 0}${card.subtitleSuffix || ''}`
            : card.subtitle;
          return (
            <div
              key={card.key}
              className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex flex-col justify-between min-h-[108px]"
            >
              <div className="flex justify-between items-start gap-2">
                <p className="text-slate-500 text-sm font-medium leading-snug">{card.label}</p>
                <div className={`p-1.5 rounded-md shrink-0 ${card.iconClass}`}>
                  <span className="material-symbols-outlined text-[20px]">{card.icon}</span>
                </div>
              </div>
              <div className="mt-3">
                {statsLoading ? (
                  <div className="h-8 w-16 rounded bg-slate-100 animate-pulse" />
                ) : (
                  <>
                    <p className="text-slate-900 text-2xl font-bold tracking-tight tabular-nums">
                      {Number(value).toLocaleString(getDateLocale())}
                    </p>
                    {subtitle ? (
                      <p className="text-slate-400 text-xs mt-1 leading-snug">{subtitle}</p>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <h3 className="text-lg font-bold text-slate-900">{t('jira.comments')}</h3>
          <span className="text-xs font-medium bg-slate-100 text-slate-600 px-2 py-1 rounded">
            {t('jira.row', { count: totalCount })}
          </span>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-slate-500">{t('common:loadingEllipsis')}</div>
          ) : error ? (
            <div className="flex items-center justify-center py-10 text-red-500">{error}</div>
          ) : comments.length === 0 ? (
            <div className="flex items-center justify-center py-10 text-slate-500">{t('jira.noComments')}</div>
          ) : (
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="px-6 py-3 font-semibold">{t('jira.commentId')}</th>
                  <th className="px-6 py-3 font-semibold">{t('jira.imputation')}</th>
                  <th className="px-6 py-3 font-semibold">{t('jira.imputationError')}</th>
                  <th className="px-6 py-3 font-semibold">{t('jira.body')}</th>
                  <th className="px-6 py-3 font-semibold">{t('jira.author')}</th>
                  <th className="px-6 py-3 font-semibold">{t('jira.email')}</th>
                  <th className="px-6 py-3 font-semibold">{t('jira.authorActive')}</th>
                  <th className="px-6 py-3 font-semibold">{t('jira.accountType')}</th>
                  <th className="px-6 py-3 font-semibold">{t('jira.visibilityType')}</th>
                  <th className="px-6 py-3 font-semibold">{t('jira.visibilityValue')}</th>
                  <th className="px-6 py-3 font-semibold">{t('jira.created')}</th>
                  <th className="px-6 py-3 font-semibold">{t('jira.updated')}</th>
                  <th className="px-6 py-3 font-semibold">{t('jira.storedAt')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {comments.map((item) => (
                  <tr
                    key={item.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => onCommentSelect?.(item)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onCommentSelect?.(item);
                      }
                    }}
                    className={`cursor-pointer transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/35 focus-visible:ring-inset ${
                      selectedComment?.id === item.id
                        ? 'bg-slate-100 hover:bg-slate-100'
                        : 'hover:bg-slate-50'
                    }`}
                  >
                    <td className="px-6 py-3 font-semibold text-slate-900">{item.commentId || '—'}</td>
                    <td className="px-6 py-3">
                      {item.has_imputation ? (
                        <span className="text-emerald-700 font-medium">{t('common:yes')}</span>
                      ) : (
                        <span className="text-amber-700 font-medium">{t('common:no')}</span>
                      )}
                    </td>
                    <td className="px-6 py-3 max-w-xs truncate" title={item.imputation_error || ''}>
                      {item.imputation_error || '—'}
                    </td>
                    <td className="px-6 py-3 max-w-xs truncate" title={item.body || ''}>{item.body || '—'}</td>
                    <td className="px-6 py-3">{item.author?.displayName || '—'}</td>
                    <td className="px-6 py-3">{item.author?.emailAddress || '—'}</td>
                    <td className="px-6 py-3">
                      {item.author?.active == null ? '—' : item.author.active ? t('common:yes') : t('common:no')}
                    </td>
                    <td className="px-6 py-3">{item.author?.accountType || '—'}</td>
                    <td className="px-6 py-3">{item.visibility?.type || '—'}</td>
                    <td className="px-6 py-3">{item.visibility?.value || '—'}</td>
                    <td className="px-6 py-3">{formatDateTime(item.created)}</td>
                    <td className="px-6 py-3">{formatDateTime(item.updated)}</td>
                    <td className="px-6 py-3">{formatDateTime(item.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-slate-100 bg-slate-50/50">
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500">
              {t('common:showingRange', { from: startRow, to: endRow, total: totalCount })}
            </span>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">{t('common:perPage')}</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="rounded border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page <= 1}
              className="h-8 w-8 rounded border border-slate-200 bg-white text-slate-500 disabled:opacity-50"
              title={t('common:firstPage')}
            >
              <span className="material-symbols-outlined text-sm">first_page</span>
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="h-8 w-8 rounded border border-slate-200 bg-white text-slate-500 disabled:opacity-50"
              title={t('common:previousPage')}
            >
              <span className="material-symbols-outlined text-sm">chevron_left</span>
            </button>
            <span className="text-sm text-slate-700 font-medium px-3">
              {t('common:pageOfWord', { page, totalPages })}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="h-8 w-8 rounded border border-slate-200 bg-white text-slate-500 disabled:opacity-50"
              title={t('common:nextPage')}
            >
              <span className="material-symbols-outlined text-sm">chevron_right</span>
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages}
              className="h-8 w-8 rounded border border-slate-200 bg-white text-slate-500 disabled:opacity-50"
              title={t('common:lastPage')}
            >
              <span className="material-symbols-outlined text-sm">last_page</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JiraCommentsPage;
